import { useController, UseControllerProps } from 'react-hook-form';
import ImageIcon from '@mui/icons-material/Image';
import {
    Box,
    FormControl,
    FormHelperText,
    Stack,
    Typography,
} from '@mui/material';

import { Image, ImageProps } from './Image';
import {
    ImageUpload,
    ImageUploadProps,
    UploadResponseWithVersion,
} from './UploadInput';

interface ImageFieldProps extends UseControllerProps {
    label?: string;
    helperText?: string;
    readOnly?: boolean;
    required?: boolean;
    imagekitProps?: Partial<ImageProps>;
    imagekitUploadProps?: Partial<ImageUploadProps>;
    height?: number | string;
    width?: number | string;
    EmptyImageComponent?: React.ComponentType<EmptyImagePlaceholderProps>;
}

/**
 * Form field component for handling image uploads using ImageKit
 *
 * @param props - Component props including react-hook-form controller props
 * @returns A form field that handles image upload and display
 */
export function ImageField({
    name,
    control,
    label,
    helperText,
    rules,
    readOnly = false,
    required = false,
    height = 'auto',
    width = '100%',
    imagekitProps,
    imagekitUploadProps,
    EmptyImageComponent = EmptyImagePlaceholder,
    ...rest
}: ImageFieldProps) {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
        rules: {
            ...rules,
            required: required ? 'This field is required' : false,
        },
    });

    const handleUploadSuccess = (response: UploadResponseWithVersion) => {
        const version = response?.versionInfo?.name.split(' ')[1];

        // Update the form field value with the URL plus version query from the upload response
        field.onChange(
            `${response.filePath}${version ? `?version=${version}` : ''}`
        );
    };

    return (
        <FormControl
            fullWidth
            error={!!error}
            variant="outlined"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {label && (
                <Typography
                    variant="subtitle2"
                    color="text.primary"
                    sx={{ mb: 1 }}
                >
                    {label} {required && '*'}
                </Typography>
            )}
            <Stack alignItems="center">
                {readOnly ? (
                    // Show the image in read-only mode
                    field.value ? (
                        <Image
                            path={field.value as string}
                            fallback={
                                <EmptyImageComponent
                                    width={width}
                                    height={height}
                                />
                            }
                            height={height}
                            width={width}
                            {...imagekitProps}
                        />
                    ) : (
                        <EmptyImageComponent width={width} height={height} />
                    )
                ) : (
                    // Show the upload component in edit mode
                    <ImageUpload
                        required={required}
                        label=""
                        error={!!error}
                        onSuccess={handleUploadSuccess}
                        height={height}
                        width={width}
                        {...rest}
                        {...imagekitUploadProps}
                        imagekitProps={imagekitProps}
                    />
                )}
            </Stack>

            {(helperText || error) && (
                <FormHelperText>
                    {error ? error.message : helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
}

interface EmptyImagePlaceholderProps {
    width: string | number;
    height: string | number;
}
// Empty placeholder when no image is available
function EmptyImagePlaceholder({ width, height }: EmptyImagePlaceholderProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.800',
                borderRadius: 1,
                height,
                width,
                minHeight: 200,
            }}
        >
            <ImageIcon color="disabled" sx={{ fontSize: 100 }} />
        </Box>
    );
}
